import { X } from "lucide-react";
import React, { useEffect, useState } from "react";
import ButtonV2 from "../../../../components/ui/button/v2";
import { cn } from "../../../../lib/utils";

interface ClosureNoteModalProps {
  isOpen: boolean;
  onClose: () => void;
  variant: "edit" | "view";
  initialNote?: string;
  onSave?: (note: string) => void;
  isSaveDisabled?: boolean;
  titleOverride?: string;
}

const ClosureNoteModal: React.FC<ClosureNoteModalProps> = ({
  isOpen,
  onClose,
  variant,
  initialNote = "",
  onSave,
  isSaveDisabled = false,
  titleOverride,
}) => {
  const [note, setNote] = useState(initialNote);

  useEffect(() => {
    setNote(initialNote);
  }, [initialNote, isOpen]);

  const handleSave = () => {
    if (onSave && note.trim()) {
      onSave(note);
      onClose();
    }
  };

  const handleDiscard = () => {
    setNote(initialNote);
    onClose();
  };

  if (!isOpen) return null;

  const isEditMode = variant === "edit";
  const isSaveButtonDisabled = isSaveDisabled || !note.trim();

  const renderHeader = () => (
    <div className="flex flex-row justify-between items-start">
      <div className="flex flex-col gap-1">
        <span className="font-sans font-semibold text-lg leading-7 text-[#0A0A0A]">
          {titleOverride ? titleOverride: isEditMode ? `Add closure note`: `Closure note`}
        </span>
      </div>
      <button
        onClick={onClose}
        className="p-1 hover:bg-gray-100 rounded-md transition-colors"
        aria-label="Close modal"
      >
        <X className="h-5 w-5 text-gray-500" />
      </button>
    </div>
  );

  const renderContentArea = () => (
    <div className="flex-1 flex flex-col">
      {isEditMode ? (
        <textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Add a note for the party..."
          className={cn(
            "w-full h-full resize-none rounded-md border border-[#E5E5E5]",
            "font-sans font-normal text-sm leading-5 text-[#0A0A0A]",
            "placeholder:text-[#737373] placeholder:font-sans placeholder:font-normal placeholder:text-sm",
            "focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          )}
          style={{
            paddingTop: "4px",
            paddingRight: "12px",
            paddingBottom: "4px",
            paddingLeft: "12px",
            letterSpacing: "0%",
          }}
        />
      ) : (
        <div
          className={cn(
            "w-full h-full overflow-auto rounded-md border border-[#E5E5E5] bg-[#F9FAFB]",
            "font-sans font-normal text-sm leading-5 text-[#0A0A0A]"
          )}
          style={{
            paddingTop: "4px",
            paddingRight: "12px",
            paddingBottom: "4px",
            paddingLeft: "12px",
            letterSpacing: "0%",
          }}
        >
          {note || "No note added"}
        </div>
      )}
    </div>
  );

  const renderDiscardButton = () => (
    <ButtonV2
      variant="outline"
      onClick={handleDiscard}
      className="font-sans font-medium text-sm leading-5"
      style={{
        width: "137.5px",
        height: "36px",
        borderColor: "#3551F3",
        color: "#3551F3",
        gap: "8px",
      }}
    >
      Discard
    </ButtonV2>
  );

  const renderSaveButton = () => (
    <ButtonV2
      variant="gradient"
      onClick={handleSave}
      disabled={isSaveButtonDisabled}
      className={cn(
        "font-sans font-medium text-sm leading-5",
        isSaveButtonDisabled && "opacity-50 cursor-not-allowed"
      )}
      style={{
        width: "137.5px",
        height: "36px",
        gap: "8px",
      }}
    >
      Save Changes
    </ButtonV2>
  );

  const renderCloseButton = () => (
    <ButtonV2
      variant="outline"
      onClick={onClose}
      className="font-sans font-medium text-sm leading-5"
      style={{
        width: "137.5px",
        height: "36px",
        borderColor: "#3551F3",
        color: "#3551F3",
        gap: "8px",
      }}
    >
      Close
    </ButtonV2>
  );

  const renderFooter = () => (
    <div className="flex flex-row justify-end items-center gap-3">
      {isEditMode ? (
        <>
          {renderDiscardButton()}
          {renderSaveButton()}
        </>
      ) : (
        renderCloseButton()
      )}
    </div>
  );

  const renderModalBody = () => (
    <div className="flex flex-col h-full gap-4">
      {renderHeader()}
      {renderContentArea()}
      {renderFooter()}
    </div>
  );

  const renderBackdrop = () => (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 z-40"
      onClick={onClose}
    />
  );

  const renderModal = () => (
    <div
      className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-50 bg-white rounded-lg shadow-lg border-[2px] border-[#E5E5E5]"
      style={{
        width: "660px",
        height: "408px",
        padding: "24px",
        gap: "16px",
      }}
      onClick={(e) => e.stopPropagation()}
    >
      {renderModalBody()}
    </div>
  );

  return (
    <>
      {renderBackdrop()}
      {renderModal()}
    </>
  );
};

export default ClosureNoteModal;
